import React, { useState } from "react"
import { useSortable } from "@dnd-kit/react/sortable"
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers"
import { Popover } from "react-tiny-popover"
import moment from "moment"
import { Task } from "~/types/sponsorship/Task"
import { Image, VerticalDots } from "~/library/UIkit/Icons"
import ConfirmationModal from "~/library/ConfirmationModal"
import EditTask from "~/views/Sponsorship/EditTask"
import TaskViewModal from "./TaskViewModal"
import { useCoordinatedPopover } from "~/hooks/useCoordinatedPopover"

interface DraggableTaskProps {
  task: Task
  index: number
  status: string
  onDeleteTask: (taskId: string) => void
  onTaskUpdated?: () => void
}

const DraggableTask: React.FC<DraggableTaskProps> = ({
  task,
  index,
  status,
  onDeleteTask,
  onTaskUpdated,
}) => {
  const { ref } = useSortable({
    id: task.id,
    index,
    type: "task",
    accept: "task",
    group: status,
    modifiers: [RestrictToVerticalAxis],
    data: {
      initialIndex: task.position,
      initialStatus: task.status,
    },
  })

  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const {
    isOpen: showPopover,
    togglePopover,
    closePopover,
    handleClickOutside,
    toggleButtonRef,
  } = useCoordinatedPopover(`task-${task.id}`)

  const handleViewActionClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setShowViewModal(true)
    closePopover()
  }

  const handleEditActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setShowEditModal(true)
    closePopover()
  }

  const handleDeleteActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setShowDeleteModal(true)
    closePopover()
  }

  return (
    <div ref={ref}>
      <div
        className="row flex-nowrap align-items-center justify-content-between py-2 cursor-pointer"
        onClick={handleViewActionClick}
      >
        <div className="col-1">
          <div
            className="d-flex align-items-center justify-content-center bg-light rounded"
            style={{ width: "40px", height: "40px" }}
          >
            <Image size={20} className="text-muted" />
          </div>
        </div>
        <div className="col-3">
          <span className="fw-semibold">{task.title}</span>
        </div>
        <div className="col-5">
          {task.description ? (
            <p className="mb-0 text-muted font-14">{task.description}</p>
          ) : (
            <span className="text-muted font-14 fst-italic">No description</span>
          )}
        </div>
        <div className="col-2 whitespace-nowrap">
          <small className="text-muted">{moment(task.createdAt).format("MMM D, YYYY")}</small>
        </div>
        <div className="col-1 text-end">
          <Popover
            isOpen={showPopover}
            padding={6}
            positions={["left", "bottom"]}
            reposition={false}
            onClickOutside={handleClickOutside}
            content={() => (
              <ul
                className="dropdown-menu"
                style={{ display: "block", position: "unset", minWidth: "unset" }}
              >
                <li>
                  <button
                    onClick={handleEditActionClick}
                    className="dropdown-item"
                    role="menuitem"
                    type="button"
                  >
                    Edit Task
                  </button>
                </li>
                <li>
                  <button
                    onClick={handleDeleteActionClick}
                    className="dropdown-item text-danger"
                    role="menuitem"
                    type="button"
                  >
                    Delete Task
                  </button>
                </li>
              </ul>
            )}
          >
            <button
              ref={toggleButtonRef}
              type="button"
              className="btn-small-icon"
              onClick={togglePopover}
              title="Actions"
              style={{ marginLeft: "auto" }}
              data-popover-toggle
            >
              <VerticalDots />
            </button>
          </Popover>
        </div>
      </div>

      {showViewModal && (
        <TaskViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          taskId={task.id}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            onDeleteTask(task.id)
          }}
          title={`Delete Task "${task.title}"?`}
          description="This action cannot be undone. Are you sure you want to delete this task?"
        />
      )}

      {showEditModal && (
        <EditTask
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={onTaskUpdated}
          taskId={task.id}
        />
      )}
    </div>
  )
}

export default DraggableTask
